import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X } from 'lucide-react';
import styles from './Navbar.module.css';
import logoNoFooter from '../../assets/logo_no_footer.png';
import initials from '../../assets/initials.png';

// Translation map for context-aware language switching
const routeMap = {
  '/es': '/en',
  '/en': '/es',
  '/es/nosotros': '/en/about',
  '/en/about': '/es/nosotros',
  '/es/servicios': '/en/services',
  '/en/services': '/es/servicios',
  '/es/proyectos': '/en/projects',
  '/en/projects': '/es/proyectos',
  '/es/contacto': '/en/contact',
  '/en/contact': '/es/contacto',
  '/es/politica-privacidad': '/en/privacy-policy',
  '/en/privacy-policy': '/es/politica-privacidad',
  '/es/tratamiento-datos': '/en/data-management',
  '/en/data-management': '/es/tratamiento-datos'
};

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const currentLang = i18n.language; // 'es' or 'en'
  const isEn = currentLang === 'en';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const switchLanguage = (targetLang) => {
    if (targetLang === currentLang) return;
    
    // Save preference
    window.localStorage.setItem('preferred_language', targetLang);
    
    // Find mapped route or fallback to root target language
    const currentPath = location.pathname.replace(/\/$/, ''); // remove trailing slash
    let targetPath = routeMap[currentPath];
    
    // If not in map but path starts with current lang, just replace it as fallback
    if (!targetPath) {
      if (currentPath.startsWith(`/${currentLang}`)) {
        targetPath = currentPath.replace(`/${currentLang}`, `/${targetLang}`);
      } else {
        targetPath = `/${targetLang}`;
      }
    }
    
    navigate(targetPath);
  };

  const navLinks = [
    { name: t('navbar.home'), path: `/${currentLang}` },
    { name: t('navbar.about'), path: `/${currentLang}/${isEn ? 'about' : 'nosotros'}` },
    { name: t('navbar.services'), path: `/${currentLang}/${isEn ? 'services' : 'servicios'}` },
    { name: t('navbar.projects'), path: `/${currentLang}/${isEn ? 'projects' : 'proyectos'}` }
  ];

  const contactPath = `/${currentLang}/${isEn ? 'contact' : 'contacto'}`;

  return (
    <nav className={`${styles.navbar} ${scrolled ? styles.scrolled : ''}`}>
      <div className={`container ${styles.navbarContainer}`}>
        <Link to={`/${currentLang}`} className={styles.logo}>
          <img
            src={logoNoFooter}
            alt="CINBIOSIS Logo"
            className={styles.logoImage}
          />
          <img
            src={initials}
            alt="CINBIOSIS"
            className={styles.logoMobile}
          />
        </Link>

        {/* Desktop Menu */}
        <div className={styles.desktopMenu}>
          {navLinks.map((link) => (
            <NavLink
              key={link.name}
              to={link.path}
              className={({ isActive }) =>
                isActive ? `${styles.navLink} ${styles.active}` : styles.navLink
              }
              end={link.path === `/${currentLang}`} // exact match for home to prevent "active" on all subs
            >
              {link.name}
            </NavLink>
          ))}
          <Link to={contactPath} className={styles.ctaButton}>
             {t('navbar.cta')}
          </Link>
          
          {/* Language Switcher */}
          <div className={styles.langSwitcher}>
            <button 
              onClick={() => switchLanguage('es')} 
              className={currentLang === 'es' ? styles.activeLang : ''}
              title="Español"
            >
              ES
            </button>
            <span className={styles.langDivider}>|</span>
            <button 
              onClick={() => switchLanguage('en')} 
              className={currentLang === 'en' ? styles.activeLang : ''}
              title="English"
            >
              EN
            </button>
          </div>
        </div>

        {/* Mobile Menu Button + Language Switcher for Mobile */}
        <div className={styles.mobileActions}>
          <div className={styles.mobileLangSwitcher}>
            <button onClick={() => switchLanguage('es')} className={currentLang === 'es' ? styles.activeLang : ''}>ES</button>
            <span>|</span>
            <button onClick={() => switchLanguage('en')} className={currentLang === 'en' ? styles.activeLang : ''}>EN</button>
          </div>
          <button className={styles.mobileMenuBtn} onClick={toggleMenu} aria-label="Toggle menu">
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`${styles.mobileMenu} ${isMenuOpen ? styles.mobileMenuOpen : ''}`}>
        {navLinks.map((link) => (
          <NavLink
            key={link.name}
            to={link.path}
            className={({ isActive }) =>
              isActive ? `${styles.mobileNavLink} ${styles.active}` : styles.mobileNavLink
            }
            onClick={() => setIsMenuOpen(false)}
            end={link.path === `/${currentLang}`}
          >
            {link.name}
          </NavLink>
        ))}
        <Link
          to={contactPath}
          className={styles.mobileCtaButton}
          onClick={() => setIsMenuOpen(false)}
        >
          {t('navbar.cta')}
        </Link>
      </div>
    </nav>
  );
};

export default Navbar;
